import { App, Form } from "antd";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";
import {
    getApiError,
    getPlanConfigurationInfo,
    getPointInfoCollection,
    getRouteMyRoutesThunk,
    getUserMeThunk,
    getUserTeamGetByManagerThunk,
    patchRouteUpdate,
    patchSchedulerSetSchedulesByPoints,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Point, Route } from "tm-shared/types";

import { ROLE_CHILDREN_MAP, ROUTES } from "@/constants";
import { useAsyncAction } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { BlockList } from "@/shared/pageComposition/BlockList";
import { getSchedulerPointScheduleDetailsManagerThunk } from "@/store/slices/manager";

import { RouteEditorFormValues } from "../components/RouteEditorForm";
import { useScheduleSelection } from "../hooks";
import {
    getRouteEditorCapabilities,
    RouteEditorCapabilities,
} from "../RouteEditor.policy";
import {
    RouteEditorEditLocationState,
    RouteEditorModalState,
} from "../RouteEditor.types";
import {
    getRouteEditorEditBlocks,
    RouteEditorEditModel,
} from "./RouteEditorEdit.blocks";

interface RouteEditorEditProps {
    currentRoute: Route;
    routeId: string;
    capabilities?: RouteEditorCapabilities;
}

export const RouteEditorEdit: FC<RouteEditorEditProps> = ({
    currentRoute,
    routeId,
    capabilities: propCapabilities,
}) => {
    const dispatch = useAppDispatch();
    const { notification } = App.useApp();

    const navigate = useNavigate();

    const role = useProtectedUserRoleSelector();
    const capabilities = useMemo(
        () => propCapabilities ?? getRouteEditorCapabilities(role),
        [propCapabilities, role],
    );

    const location = useLocation() as Location & {
        state: RouteEditorEditLocationState;
    };

    const userId = useAppSelector((s) => s.user.id);
    const employee = useAppSelector((s) => s.user.employee);
    const currentScheduleList = useAppSelector(
        (s) => s.manager.currentScheduleList,
    );
    const [points, setPoints] = useState<Record<string, Point>>({});

    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState<boolean>(false);
    const [modalState, setModalState] = useState<RouteEditorModalState>(null);
    const [isDeleteBlocked, setIsDeleteBlocked] = useState<boolean>(false);
    const [isRouteSwapWarningOpen, setIsRouteSwapWarningOpen] = useState(false);

    const [routeEditorForm] = Form.useForm<RouteEditorFormValues>();

    useEffect(() => {
        if (location?.state) {
            routeEditorForm.setFieldsValue(location?.state?.formValues);
            setIsDeleteBlocked(location?.state?.isDeleteBlocked);
        }
    }, [location.key, location?.state, routeEditorForm]);

    const selection = useScheduleSelection({ points });

    const [handleSave, saveLoading] = useAsyncAction(
        async (force?: boolean) => {
            const { routeAgent, routeName } = routeEditorForm.getFieldsValue();

            const agentId = capabilities.canEditAgent
                ? routeAgent
                : currentRoute.agentInfo.id;

            try {
                await patchRouteUpdate({
                    name: routeName,
                    id: routeId,
                    ...(currentRoute.agentInfo.id !== agentId
                        ? {
                              newAgentId: agentId,
                          }
                        : {}),
                    force,
                });

                await patchSchedulerSetSchedulesByPoints({
                    routeId,
                    schedules: currentScheduleList,
                });

                if (capabilities.canEditAgent) {
                    navigate(ROUTES.RouteManagement);
                } else {
                    await dispatch(getUserMeThunk()).unwrap();
                }

                notification.success({
                    title: "Маршрут збережено",
                });
            } catch (error) {
                const { displayMessage, code } = getApiError(error);

                if (code === "-32020") {
                    setIsRouteSwapWarningOpen(true);
                    return;
                }

                notification.error({
                    title: "Не вдалося зберегти маршрут",
                    description: displayMessage,
                });
            }
        },
    );

    const refreshSchedules = useCallback(async () => {
        try {
            setSchedulesLoading(true);
            const { result: schedules } = await dispatch(
                getSchedulerPointScheduleDetailsManagerThunk({
                    routeId: currentRoute.uuid,
                }),
            ).unwrap();

            const { result: pointsData } = await getPointInfoCollection({
                ids: schedules?.map((item) => item?.pointId),
            });

            const pointsMap = (pointsData ?? []).reduce<Record<string, Point>>(
                (acc, point) => {
                    acc[point.id] = point;
                    return acc;
                },
                {},
            );

            setPoints(pointsMap);
        } catch (error) {
            console.error(error);
        } finally {
            setSchedulesLoading(false);
        }
    }, [dispatch, currentRoute.uuid]);

    useEffect(() => {
        refreshSchedules();
    }, [location.key, refreshSchedules]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setInitialLoading(true);

                if (employee?.context?.areaId && employee?.context?.clientId) {
                    await getPlanConfigurationInfo({
                        mainRegionId: employee?.context?.areaId,
                        partnerId: employee?.context?.clientId,
                        targetDate: dayjs()
                            .startOf("month")
                            .format(backendDateFormat.date),
                    })
                        .then(({ result }) => {
                            if (result?.meta?.approvedAt)
                                setIsDeleteBlocked(true);
                        })
                        .catch(console.log);
                }

                await dispatch(
                    getRouteMyRoutesThunk({
                        ownerId: userId,
                        roleSlug: ROLE_CHILDREN_MAP[role]!,
                    }),
                );

                if (capabilities.canEditAgent) {
                    const teamFromResponse = await dispatch(
                        getUserTeamGetByManagerThunk({ managerId: userId }),
                    ).unwrap();

                    const isExist = teamFromResponse?.result?.members?.some(
                        (member) =>
                            member?.userId === currentRoute.agentInfo.id,
                    );

                    routeEditorForm.setFieldsValue({
                        routeName: currentRoute.name || "",
                        routeAgent: isExist
                            ? currentRoute.agentInfo.id
                            : undefined,
                    });
                } else {
                    routeEditorForm.setFieldsValue({
                        routeName: currentRoute.name || "",
                    });
                }
            } catch (err) {
                console.log(err);
            } finally {
                setInitialLoading(false);
            }
        };

        if (!location.state) {
            loadInitialData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const model: RouteEditorEditModel = useMemo(
        () => ({
            capabilities,
            currentRoute,
            initialLoading,
            schedulesLoading,
            saveLoading,
            isDeleteBlocked,
            points,
            modalState,
            isRouteSwapWarningOpen,
            routeEditorForm,
            selection,
            handlers: {
                handleSave,
                setModalState,
                setIsRouteSwapWarningOpen,
                refreshSchedules,
            },
        }),
        [
            capabilities,
            currentRoute,
            initialLoading,
            schedulesLoading,
            saveLoading,
            isDeleteBlocked,
            points,
            modalState,
            isRouteSwapWarningOpen,
            routeEditorForm,
            selection,
            handleSave,
            refreshSchedules,
        ],
    );

    const blocks = useMemo(() => getRouteEditorEditBlocks(model), [model]);

    return <BlockList blocks={blocks} model={model} />;
};
